import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { APIHOST } from '../../../common';
import { getToken } from '../../../components/auth/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';

interface OrderRoomItem {
  id?: number | string;
  documentId?: string;
  hotelConfirmation?: string | null;
  type?: string | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  conference_hotel_room_night?: {
    date?: string | null;
  } | null;
  conference_hotel_room?: {
    name?: string | null;
  } | null;
  order?: {
    orderStatus?: string | null;
    conference_hotel?: {
      hotel?: {
        documentId?: string | null;
        name?: string | null;
      } | null;
    } | null;
  } | null;
}

interface StrapiListResponse<T> {
  data: T[];
  meta?: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface HotelSummary {
  hotelName: string;
  hotelDocumentId?: string;
  bookingCount: number;
  confirmedBookingCount: number;
  unconfirmedBookingCount: number;
  roomTypeNightCounts: Record<string, Record<string, number>>;
  roomTypeTotals: Record<string, number>;
  dateTotals: Record<string, number>;
  roomTypes: string[];
  dates: string[];
  grandTotal: number;
}

const isIsoDate = (value?: string | null): value is string => {
  if (!value) {
    return false;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const formatDateWithoutYear = (date: string) => {
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) {
    return date;
  }
  return `${month}/${day}`;
};

const getStayDates = (row: OrderRoomItem): string[] => {
  if (isIsoDate(row?.conference_hotel_room_night?.date)) {
    return [row.conference_hotel_room_night.date];
  }

  if (!isIsoDate(row.arrivalDate) || !isIsoDate(row.departureDate)) {
    return ['Unknown Date'];
  }

  const arrival = new Date(`${row.arrivalDate}T00:00:00`);
  const departure = new Date(`${row.departureDate}T00:00:00`);

  if (Number.isNaN(arrival.getTime()) || Number.isNaN(departure.getTime()) || departure <= arrival) {
    return ['Unknown Date'];
  }

  const dates: string[] = [];
  const current = new Date(arrival);

  while (current < departure) {
    const y = current.getFullYear();
    const m = `${current.getMonth() + 1}`.padStart(2, '0');
    const d = `${current.getDate()}`.padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    current.setDate(current.getDate() + 1);
  }

  return dates.length > 0 ? dates : ['Unknown Date'];
};

const DEPARTURE_DATE_CUTOFF = '2026-06-01';

export default function BookingReport() {
  const [orderRooms, setOrderRooms] = useState<OrderRoomItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedPercent, setLoadedPercent] = useState(0);

const fetchOrderRooms = async () => {
      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const allRows: OrderRoomItem[] = [];
        let page = 1;
        let pageCount = 1;

        while (page <= pageCount) {
          const response = await fetch(
            `${APIHOST}/api/order-rooms?filters[order][orderStatus][$eq]=received&filters[departureDate][$gte]=${DEPARTURE_DATE_CUTOFF}&populate[conference_hotel_room]=true&populate[order][populate][conference_hotel][populate][hotel]=true&pagination[page]=${page}&pagination[pageSize]=200`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error('Failed to fetch order room report data');
          }

          const payload: StrapiListResponse<OrderRoomItem> = await response.json();
          const rows = Array.isArray(payload?.data) ? payload.data : [];
          
          allRows.push(...rows);

          pageCount = payload?.meta?.pagination?.pageCount || 1;
          page += 1;
          setLoadedPercent(Math.round((page / pageCount) * 100));
        }

        // Keep explicit local filters as a guard in case backend query behavior changes.
        setOrderRooms(
          allRows.filter((row) => {
            if (row?.order?.orderStatus !== 'received') {
              return false;
            }

            if (!isIsoDate(row.departureDate)) {
              return true;
            }

            return row.departureDate >= DEPARTURE_DATE_CUTOFF;
          })
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchOrderRooms();
  }, []);

  const hotelSummaries = useMemo<HotelSummary[]>(() => {
    const grouped: Record<string, HotelSummary> = {};

    for (const row of orderRooms) {
      const hotelName = row?.order?.conference_hotel?.hotel?.name || 'Unknown Hotel';
      const hotelDocumentId = row?.order?.conference_hotel?.hotel?.documentId || undefined;
      const roomType = row?.conference_hotel_room?.name || 'Unknown';

      if (!grouped[hotelName]) {
        grouped[hotelName] = {
          hotelName,
          hotelDocumentId,
          bookingCount: 0,
          confirmedBookingCount: 0,
          unconfirmedBookingCount: 0,
          roomTypeNightCounts: {},
          roomTypeTotals: {},
          dateTotals: {},
          roomTypes: [],
          dates: [],
          grandTotal: 0,
        };
      }

      const summary = grouped[hotelName];
      if (!summary.hotelDocumentId && hotelDocumentId) {
        summary.hotelDocumentId = hotelDocumentId;
      }
      const stayDates = getStayDates(row);
      const hasHotelConfirmation = Boolean(row.hotelConfirmation && row.hotelConfirmation.trim());

      summary.bookingCount += 1;
      if (hasHotelConfirmation) {
        summary.confirmedBookingCount += 1;
      } else {
        summary.unconfirmedBookingCount += 1;
      }

      if (!summary.roomTypeNightCounts[roomType]) {
        summary.roomTypeNightCounts[roomType] = {};
      }

      for (const date of stayDates) {
        summary.roomTypeNightCounts[roomType][date] = (summary.roomTypeNightCounts[roomType][date] || 0) + 1;
        summary.roomTypeTotals[roomType] = (summary.roomTypeTotals[roomType] || 0) + 1;
        summary.dateTotals[date] = (summary.dateTotals[date] || 0) + 1;
        summary.grandTotal += 1;
      }
    }

    return Object.values(grouped)
      .map((summary) => {
        const roomTypes = Object.keys(summary.roomTypeNightCounts).sort((a, b) => a.localeCompare(b));
        const dates = Object.keys(summary.dateTotals).sort((a, b) => {
          if (a === 'Unknown Date') return 1;
          if (b === 'Unknown Date') return -1;
          return a.localeCompare(b);
        });

        return {
          ...summary,
          roomTypes,
          dates,
        };
      })
      .sort((a, b) => a.hotelName.localeCompare(b.hotelName));
  }, [orderRooms]);

  const allDates = useMemo(() => {
    const dateSet = new Set<string>();

    hotelSummaries.forEach((summary) => {
      summary.dates.forEach((date) => dateSet.add(date));
    });

    return Array.from(dateSet).sort((a, b) => {
      if (a === 'Unknown Date') return 1;
      if (b === 'Unknown Date') return -1;
      return a.localeCompare(b);
    });
  }, [hotelSummaries]);

  const exportTotals = useMemo(() => {
    const totalByDate = allDates.reduce<Record<string, number>>((acc, date) => {
      acc[date] = hotelSummaries.reduce((sum, summary) => sum + (summary.dateTotals[date] || 0), 0);
      return acc;
    }, {});

    const grandTotal = hotelSummaries.reduce((sum, summary) => sum + summary.grandTotal, 0);

    return {
      totalByDate,
      grandTotal,
    };
  }, [allDates, hotelSummaries]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 4 }}>
        <LinearProgress value={loadedPercent}  variant="determinate" />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          Loading {loadedPercent}% complete...
        </Box>
      </Box>
    );
  }

  const exportByRoom = () => {
    const csvRows = [
      ['Hotel Name', 'Room Type', ...allDates, 'Total Room Nights'],
      ...hotelSummaries.flatMap((summary) =>
        summary.roomTypes.map((roomType) => [
          summary.hotelName,
          roomType,
          ...allDates.map((date) => (summary.roomTypeNightCounts[roomType]?.[date] || 0).toString()),
          (summary.roomTypeTotals[roomType] || 0).toString(),
        ])
      ),
      ['Total', '', ...allDates.map((date) => (exportTotals.totalByDate[date] || 0).toString()), exportTotals.grandTotal.toString()],
    ];

    const csvContent = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'booking_report_by_room.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const exportByHotel = () => {
    const csvRows = [
      ['Hotel Name', ...allDates, 'Total Room Nights'],
      ...hotelSummaries.map((summary) => [
        summary.hotelName,
        ...allDates.map((date) => (summary.dateTotals[date] || 0).toString()),
        summary.grandTotal.toString(),
      ]),
      ['Total', ...allDates.map((date) => (exportTotals.totalByDate[date] || 0).toString()), exportTotals.grandTotal.toString()],
    ];

    const csvContent = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'booking_report_by_hotel.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }


  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (hotelSummaries.length === 0) {
    return <Alert severity="info">No received room bookings found.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      <Box>
        <Button disabled={loading} variant="contained" size="small" color="primary" onClick={exportByRoom}>
          Export with Room Types
        </Button>
        <Button disabled={loading} variant="contained" size="small" color="primary" sx={{ ml: 2 }} onClick={exportByHotel}>
          Export Hotel Totals Only
        </Button>
        <IconButton disabled={loading} color="primary" onClick={() =>fetchOrderRooms()}>
          <RefreshIcon />
        </IconButton>
      </Box>
      <Typography variant="h5">Hotel Room Booking Report</Typography>

      {hotelSummaries.map((summary) => {
        return (
          <Paper key={summary.hotelName} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {summary.hotelName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Bookings: {summary.bookingCount} | Confirmed: {summary.confirmedBookingCount} | Unconfirmed: {summary.unconfirmedBookingCount}
              {summary.hotelDocumentId ? (
                <>
                  {' '}|{' '}
                  <Link href={`/admin/bookings?hotelId=${encodeURIComponent(summary.hotelDocumentId)}`} underline="hover">
                    Manage Bookings
                  </Link>
                </>
              ) : null}
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Room Type</TableCell>
                  {summary.dates.map((date) => (
                    <TableCell key={`${summary.hotelName}-${date}`} align="right">
                      {date === 'Unknown Date' ? date : formatDateWithoutYear(date)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.roomTypes.map((roomType) => (
                  <TableRow key={`${summary.hotelName}-${roomType}`}>
                    <TableCell>{roomType}</TableCell>
                    {summary.dates.map((date) => (
                      <TableCell key={`${summary.hotelName}-${roomType}-${date}`} align="right">
                        {summary.roomTypeNightCounts[roomType]?.[date] || 0}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {summary.roomTypeTotals[roomType] || 0}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  {summary.dates.map((date) => (
                    <TableCell key={`${summary.hotelName}-total-${date}`} align="right" sx={{ fontWeight: 700 }}>
                      {summary.dateTotals[date] || 0}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {summary.grandTotal}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        );
      })}
    </Box>
  );
}
